import React, { useState, useEffect, useRef } from 'react'
import pako from 'pako'
/** Local Express API (apiClient); `supabase` is the legacy export name from @/lib/supabase */
import { supabase as api } from '@/lib/supabase'
import { projectsApi } from '@/lib/projectsApi'
import { downloadRelease, type ReleaseDownloadMode } from '@/lib/releaseDownload'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Package,
  Upload,
  X,
  Copy,
  Check,
  Loader2,
  ArrowLeft,
  Pencil,
  Save,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { sanitizeForILIKE } from '@/lib/security'
import { isSuperAdmin } from '@/lib/roles'
import { useTranslation } from 'react-i18next'

interface Release {
  id: number
  version: string
  release_notes: string | null
  installer_file_name: string
  installer_file_path: string
  installer_file_size: number
  installer_mime_type: string
  is_compressed: boolean | null
  original_file_size: number | null
  is_split: boolean | null
  total_chunks: number | null
  created_by: string | null
  created_at: string
  updated_at: string
}

const pageSize = 25
/** Max rows to load for client-side search/filter (Express API has no .or() on QueryBuilder) */
const MAX_RELEASES_FETCH = 5000

export const PentestReleasesPage: React.FC = () => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const { user } = useAuth()
  const navigate = useNavigate()
  const isSuper = isSuperAdmin(user)

  const [releases, setReleases] = useState<Release[]>([])
  const [releasesLoading, setReleasesLoading] = useState(false)
  const [releasesPage, setReleasesPage] = useState(1)
  const [releasesTotal, setReleasesTotal] = useState(0)
  const [releasesSearch, setReleasesSearch] = useState('')
  const [editingReleaseId, setEditingReleaseId] = useState<number | null>(null)
  const [editingReleaseNotes, setEditingReleaseNotes] = useState('')
  const [savingReleaseNotes, setSavingReleaseNotes] = useState(false)
  const [uploadingRelease, setUploadingRelease] = useState(false)
  const [downloadingReleaseId, setDownloadingReleaseId] = useState<number | null>(null)
  const [downloadMode, setDownloadMode] = useState<ReleaseDownloadMode | null>(null)
  const [downloadProgress, setDownloadProgress] = useState<{ done: number; total: number } | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [newVersion, setNewVersion] = useState('')
  const [newReleaseNotes, setNewReleaseNotes] = useState('')
  const [selectedInstallerFile, setSelectedInstallerFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState<string>('')
  const [copiedUrl, setCopiedUrl] = useState(false)

  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchDebounceInit = useRef(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // per-release download availability (server-computed: R2 key or chunk
  // files on disk). null until loaded; fail-open (missing/failed fetch
  // leaves buttons enabled, matching the old behavior) so an availability
  // hiccup never blocks a working download
  const [availability, setAvailability] = useState<Record<number, boolean> | null>(null)
  useEffect(() => {
    let cancelled = false
    projectsApi
      .getReleaseAvailability()
      .then(rows => {
        if (cancelled) return
        const map: Record<number, boolean> = {}
        for (const r of rows) map[r.id] = r.available
        setAvailability(map)
      })
      .catch(err => console.error('[releases] availability fetch failed:', err))
    return () => { cancelled = true }
  }, [refreshKey])
  const isAvailable = (id: number) => availability?.[id] !== false

  useEffect(() => {
    if (!searchDebounceInit.current) {
      searchDebounceInit.current = true
      setDebouncedSearch(releasesSearch.trim())
      return
    }
    const t = setTimeout(() => {
      setDebouncedSearch(releasesSearch.trim())
      setReleasesPage(1)
    }, 400)
    return () => clearTimeout(t)
  }, [releasesSearch])

  useEffect(() => {
    let cancelled = false
    async function run() {
      setReleasesLoading(true)
      try {
        const { data, error } = await api
          .from('releases')
          .select('*')
          .order('created_at', { ascending: false })
          .range(0, MAX_RELEASES_FETCH - 1)

        if (cancelled) return
        if (error) throw error

        let rows = (data as Release[]) || []
        if (debouncedSearch.trim()) {
          const q = sanitizeForILIKE(debouncedSearch).toLowerCase()
          if (q) {
            rows = rows.filter(
              (r) =>
                (r.version && String(r.version).toLowerCase().includes(q)) ||
                (r.release_notes && String(r.release_notes).toLowerCase().includes(q)) ||
                (r.installer_file_name && String(r.installer_file_name).toLowerCase().includes(q))
            )
          }
        }

        const total = rows.length
        const start = (releasesPage - 1) * pageSize
        setReleases(rows.slice(start, start + pageSize))
        setReleasesTotal(total)
      } catch (err: any) {
        console.error('Error fetching releases:', err)
        if (!cancelled) showError(err.message || 'Failed to fetch releases')
      } finally {
        if (!cancelled) setReleasesLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [releasesPage, debouncedSearch, refreshKey])

  const handleUpdateReleaseNotes = async (releaseId: number) => {
    setSavingReleaseNotes(true)
    try {
      const { error } = await api
        .from('releases')
        .update({ release_notes: editingReleaseNotes.trim() || null })
        .eq('id', releaseId)
      if (error) throw error
      setReleases((prev) =>
        prev.map((r) => (r.id === releaseId ? { ...r, release_notes: editingReleaseNotes.trim() || null } : r))
      )
      setEditingReleaseId(null)
      success('Release notes updated')
    } catch (error: any) {
      showError(error.message || 'Failed to update release notes')
    } finally {
      setSavingReleaseNotes(false)
    }
  }

  const handleReleaseUpload = async () => {
    if (!isSuper) {
      showError('Access Denied', 'Only super admins can upload releases')
      return
    }

    if (!newVersion.trim()) {
      showError('Version is required')
      return
    }
    if (!selectedInstallerFile) {
      showError('Installer file is required')
      return
    }

    setUploadingRelease(true)
    setUploadProgress('Reading file...')
    try {
      const { data: { user: authUser } } = await api.auth.getUser()

      const originalSize = selectedInstallerFile.size
      const maxSize = 500 * 1024 * 1024

      if (originalSize > 500 * 1024 * 1024) {
        showError('File too large', `File size (${formatFileSize(originalSize)}) exceeds 500MB. Please use a smaller installer file.`)
        setUploadingRelease(false)
        setUploadProgress('')
        return
      }

      setUploadProgress('Reading file...')
      const arrayBuffer = await selectedInstallerFile.arrayBuffer()

      setUploadProgress('Compressing file (this may take a moment)...')

      const shouldCompress = originalSize > 5 * 1024 * 1024 || originalSize > maxSize * 0.8

      let uploadData: Uint8Array
      let isCompressed = false
      let mimeType = selectedInstallerFile.type || 'application/x-msdownload'
      let finalSize = originalSize

      if (shouldCompress) {
        setUploadProgress('Compressing...')
        const compressed = pako.gzip(new Uint8Array(arrayBuffer), { level: 9 })
        if (compressed.length < originalSize) {
          uploadData = compressed
          isCompressed = true
          mimeType = 'application/gzip'
          finalSize = compressed.length
          setUploadProgress(`Compressed: ${formatFileSize(originalSize)} → ${formatFileSize(finalSize)}`)
        } else {
          uploadData = new Uint8Array(arrayBuffer)
          setUploadProgress('Compression not beneficial, using original')
        }
      } else {
        uploadData = new Uint8Array(arrayBuffer)
      }

      if (finalSize > maxSize) {
        showError('File too large', `File size (${formatFileSize(finalSize)}) exceeds the 500MB limit.`)
        setUploadingRelease(false)
        setUploadProgress('')
        return
      }

      const CHUNK_SIZE = 128 * 1024
      const bucketName = 'releases'
      const baseKey = `${newVersion}_${Date.now()}`
      const ext = isCompressed ? 'gz' : (selectedInstallerFile.name.split('.').pop() ?? 'bin')
      const totalChunks = Math.ceil(uploadData.length / CHUNK_SIZE)
      const isSplit = totalChunks > 1

      setUploadProgress(`Uploading ${isSplit ? `${totalChunks} chunks` : 'file'}...`)

      const chunkPaths: string[] = []
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, uploadData.length)
        const chunkBlob = new Blob([uploadData.slice(start, end)], { type: mimeType })
        const chunkPath = isSplit ? `${baseKey}_chunk_${i}.${ext}` : `${baseKey}.${ext}`

        setUploadProgress(
          `Uploading${isSplit ? ` chunk ${i + 1}/${totalChunks}` : ''}... (${formatFileSize(end)} / ${formatFileSize(uploadData.length)})`
        )

        let chunkError: any = null
        for (let attempt = 0; attempt < 3; attempt++) {
          if (attempt > 0) {
            setUploadProgress(`Chunk ${i + 1}/${totalChunks} retry ${attempt}...`)
            await new Promise((r) => setTimeout(r, 1000 * attempt))
          }
          const result = await api.storage
            .from(bucketName)
            .upload(chunkPath, chunkBlob, { cacheControl: '3600', upsert: attempt > 0 })
          chunkError = result.error
          if (!chunkError) break
        }
        if (chunkError) throw chunkError
        chunkPaths.push(chunkPath)
      }

      setUploadProgress('Saving release information...')

      const { data: releaseData, error: dbError } = await api
        .from('releases')
        .insert({
          version: newVersion.trim(),
          release_notes: newReleaseNotes.trim() || null,
          installer_file_name: selectedInstallerFile.name,
          installer_file_path: chunkPaths[0],
          installer_file_size: finalSize,
          installer_mime_type: mimeType,
          is_compressed: isCompressed,
          original_file_size: isCompressed ? originalSize : null,
          is_split: isSplit,
          total_chunks: isSplit ? totalChunks : null,
          created_by: authUser?.id || null,
        })
        .select()
        .single()

      if (dbError) throw dbError

      if (isSplit && releaseData) {
        const chunkRecords = chunkPaths.map((path, index) => ({
          release_id: releaseData.id,
          chunk_index: index,
          chunk_file_path: path,
          chunk_file_size: Math.min(CHUNK_SIZE, uploadData.length - index * CHUNK_SIZE),
          total_chunks: totalChunks,
        }))
        const { error: chunksError } = await api.from('release_file_chunks').insert(chunkRecords)
        if (chunksError) throw chunksError
      }

      setNewVersion('')
      setNewReleaseNotes('')
      setSelectedInstallerFile(null)
      setShowUploadModal(false)

      setRefreshKey((k) => k + 1)
      success(`Release uploaded successfully!${isSplit ? ` (${totalChunks} chunks)` : ''}`)
    } catch (error: any) {
      console.error('Error uploading release:', error)
      showError(error.message || 'Failed to upload release')
    } finally {
      setUploadingRelease(false)
    }
  }

  const handleReleaseDownload = async (release: Release) => {
    if (downloadingReleaseId === release.id) return
    if (!isAvailable(release.id)) {
      showError('Unavailable', 'Installer files for this release are no longer stored.')
      return
    }

    setDownloadingReleaseId(release.id)
    setDownloadProgress(null)

    try {
      const mode = await downloadRelease(
        release,
        (loaded, total) => {
          setDownloadProgress({ done: loaded, total })
        },
        setDownloadMode,
      )
      if (mode === 'r2') {
        success(`Download started: ${release.installer_file_name}`)
      } else {
        success(`Downloaded ${release.installer_file_name}`)
      }
    } catch (error: any) {
      console.error('Error downloading release:', error)
      showError(error.message || 'Failed to download release')
    } finally {
      setDownloadingReleaseId(null)
      setDownloadMode(null)
      setDownloadProgress(null)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')} className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                <span className="truncate">{t('dashboardPage.pentestReleases')}</span>
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                Installers and notes for the penetration testing client (local API — same list as the old Releases tab)
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-none w-full">
        <Card className="mb-6 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('releasesPage.releaseNotes')}
                  value={releasesSearch}
                  onChange={(e) => setReleasesSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRefreshKey((k) => k + 1)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div>
                <CardTitle>{t('dashboardPage.pentestReleases')}</CardTitle>
                <CardDescription>{t('releasesPage.pentestDescription')}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const url = `${window.location.origin}/pentest-releases`
                    navigator.clipboard.writeText(url)
                    setCopiedUrl(true)
                    success('URL copied to clipboard!')
                    setTimeout(() => setCopiedUrl(false), 2000)
                  }}
                >
                  {copiedUrl ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Link
                    </>
                  )}
                </Button>
                {isSuper && (
                  <Button onClick={() => setShowUploadModal(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Release
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {releasesLoading ? (
              <div className="text-center py-8">{t('releasesPage.loading')}</div>
            ) : releases.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">{t('releasesPage.empty')}</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 dark:bg-slate-700">
                      <tr>
                        <th className="text-left p-3 font-medium">{t('releasesPage.version')}</th>
                        <th className="text-left p-3 font-medium">{t('releasesPage.installerFile')}</th>
                        <th className="text-left p-3 font-medium hidden md:table-cell">MB</th>
                        <th className="text-left p-3 font-medium hidden lg:table-cell">{t('releasesPage.releaseNotes')}</th>
                        <th className="text-left p-3 font-medium hidden xl:table-cell">{t('fields.createdAt')}</th>
                        <th className="text-right p-3 font-medium">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {releases.map((release) => (
                        <tr
                          key={release.id}
                          className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                        >
                          <td className="p-3 font-medium">{release.version}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-slate-500" />
                              <span className="font-mono text-xs">{release.installer_file_name}</span>
                            </div>
                          </td>
                          <td className="p-3 hidden md:table-cell text-slate-500 dark:text-slate-400">
                            <div className="flex flex-col">
                              <span>{formatFileSize(release.installer_file_size)}</span>
                              {release.is_compressed && release.original_file_size && (
                                <span className="text-xs text-slate-400">
                                  Original: {formatFileSize(release.original_file_size)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            {editingReleaseId === release.id ? (
                              <div className="flex flex-col gap-2">
                                <textarea
                                  className="w-full text-sm border border-slate-300 dark:border-slate-600 rounded p-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500"
                                  value={editingReleaseNotes}
                                  onChange={(e) => setEditingReleaseNotes(e.target.value)}
                                  placeholder={t('releasesPage.releaseNotes')}
                                  autoFocus
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleUpdateReleaseNotes(release.id)}
                                    disabled={savingReleaseNotes}
                                  >
                                    {savingReleaseNotes ? (
                                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                    ) : (
                                      <Save className="h-3 w-3 mr-1" />
                                    )}
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingReleaseId(null)}
                                    disabled={savingReleaseNotes}
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2 group">
                                {release.release_notes ? (
                                  <Link
                                    to={`/release-notes/${encodeURIComponent(release.version)}`}
                                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                                  >
                                    View Release Notes
                                  </Link>
                                ) : (
                                  <span className="text-slate-400 text-sm">{t('releasesPage.noNotes')}</span>
                                )}
                                {isSuper && (
                                  <button
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0"
                                    title={t('releasesPage.edit')}
                                    type="button"
                                    onClick={() => {
                                      setEditingReleaseId(release.id)
                                      setEditingReleaseNotes(release.release_notes ?? '')
                                    }}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3 hidden xl:table-cell text-slate-500 dark:text-slate-400">
                            {formatDate(release.created_at)}
                          </td>
                          <td className="p-3">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReleaseDownload(release)}
                                disabled={downloadingReleaseId === release.id || !isAvailable(release.id)}
                                title={isAvailable(release.id) ? undefined : 'Installer files for this release are no longer stored'}
                              >
                                {downloadingReleaseId === release.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    {downloadProgress
                                      ? `${downloadProgress.done}/${downloadProgress.total}`
                                      : 'Downloading...'}
                                  </>
                                ) : (
                                  <>
                                    <Download className="h-4 w-4 mr-1" />
                                    Download
                                  </>
                                )}
                              </Button>
                            </div>
                            {!isAvailable(release.id) && (
                              <p className="mt-1.5 text-[11px] leading-snug text-amber-600 dark:text-amber-500 text-right max-w-xs ml-auto">
                                Unavailable — installer files no longer stored
                              </p>
                            )}
                            {downloadingReleaseId === release.id && downloadMode === 'chunks' && (
                              <p className="mt-1.5 text-[11px] leading-snug text-slate-500 text-right max-w-xs ml-auto">
                                Large file — this may take a few minutes.
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Showing {(releasesPage - 1) * pageSize + 1} to {Math.min(releasesPage * pageSize, releasesTotal)} of{' '}
                    {releasesTotal} releases
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReleasesPage((p) => Math.max(1, p - 1))}
                      disabled={releasesPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReleasesPage((p) => p + 1)}
                      disabled={releasesPage * pageSize >= releasesTotal}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 mt-6">
          <CardHeader>
            <CardTitle>{t('releasesPage.systemInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">{t('releasesPage.systemRequirements')}</h3>
              <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                <li>
                  <strong>{t('releasesPage.operatingSystem')}:</strong> Windows 10/11 (64-bit)
                </li>
                <li>
                  <strong>.NET Runtime:</strong> Included (self-contained deployment)
                </li>
                <li>
                  <strong>{t('releasesPage.privileges')}:</strong> {t('releasesPage.privilegesDetail')}
                </li>
                <li>
                  <strong>{t('releasesPage.network')}:</strong> {t('releasesPage.networkDetail')}
                </li>
                <li>
                  <strong>{t('fields.hardware')}:</strong> {t('releasesPage.hardwareDetail')}
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">{t('releasesPage.knownIssues')}</h3>
              <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                <li>{t('releasesPage.issueAntivirus')}</li>
                <li>{t('releasesPage.issueRate')}</li>
                <li>{t('releasesPage.issueFont')}</li>
              </ul>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 italic">
                <strong>{t('fields.notes')}:</strong> {t('releasesPage.resolvedNote')}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">{t('releasesPage.support')}</h3>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                For issues, questions, or feature requests, please contact the development team or visit the project repository.
              </p>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                <strong>Copyright(C) SeaNet Co., Ltd. All right reserved</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-2xl w-full">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{t('releasesPage.uploadNew')}</CardTitle>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadModal(false)
                      setNewVersion('')
                      setNewReleaseNotes('')
                      setSelectedInstallerFile(null)
                    }}
                    className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <CardDescription>{t('releasesPage.uploadDescription')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('releasesPage.version')} *</label>
                  <Input placeholder="e.g., 1.0.0" value={newVersion} onChange={(e) => setNewVersion(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('releasesPage.releaseNotes')} <span className="text-xs text-slate-500">(Markdown)</span>
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-mono text-sm"
                    rows={8}
                    placeholder={t('releasesPage.markdownPlaceholder')}
                    value={newReleaseNotes}
                    onChange={(e) => setNewReleaseNotes(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">{t('releasesPage.markdownHelp')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('releasesPage.installerFile')} (EXE) *</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".exe"
                      onChange={(e) => setSelectedInstallerFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="pentest-installer-upload"
                    />
                    <label
                      htmlFor="pentest-installer-upload"
                      className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose File
                    </label>
                    {selectedInstallerFile && (
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        <div>{selectedInstallerFile.name}</div>
                        <div className="text-xs mt-1">
                          Size: {formatFileSize(selectedInstallerFile.size)}
                          {selectedInstallerFile.size > 10 * 1024 * 1024 && (
                            <span className="ml-2 text-blue-600 dark:text-blue-400">(Will be compressed to reduce size)</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {uploadProgress ? <p className="text-sm text-slate-600 dark:text-slate-400">{uploadProgress}</p> : null}
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowUploadModal(false)
                      setNewVersion('')
                      setNewReleaseNotes('')
                      setSelectedInstallerFile(null)
                      setUploadProgress('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReleaseUpload}
                    disabled={uploadingRelease || !newVersion.trim() || !selectedInstallerFile}
                  >
                    {uploadingRelease ? 'Uploading...' : 'Upload Release'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
